import { useEffect, useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  MouseEvent,
  PointerEvent,
  ReactNode,
} from 'react';
import LiquidGlass from 'liquid-glass-react';
import { ArrowRight, Loader2 } from 'lucide-react';
import './LiquidCreateButton.css';

type LiquidCreateButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  loading?: boolean;
  label?: string;
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  showArrow?: boolean;
  variant?: 'dark' | 'lavender';
};

function usePrefersReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() => (
    typeof window !== 'undefined'
      && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
  ));

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;

    const updatePreference = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    media.addEventListener?.('change', updatePreference);
    return () => media.removeEventListener?.('change', updatePreference);
  }, []);

  return reducedMotion;
}

function useAnimatedDisplacement(target: number, reducedMotion: boolean) {
  const [value, setValue] = useState(28);
  const currentValue = useRef(28);

  useEffect(() => {
    if (reducedMotion) {
      currentValue.current = 28;
      setValue(28);
      return undefined;
    }

    const from = currentValue.current;
    const startedAt = performance.now();
    const duration = 260;
    let frameId = 0;

    const animate = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = from + (target - from) * eased;
      currentValue.current = nextValue;
      setValue(nextValue);

      if (progress < 1) frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [reducedMotion, target]);

  return value;
}

export default function LiquidCreateButton({
  loading = false,
  label = '开始创作',
  loadingLabel = '正在创建',
  leadingIcon,
  showArrow = true,
  variant = 'dark',
  disabled = false,
  className = '',
  type = 'button',
  onMouseEnter,
  onMouseLeave,
  onPointerMove,
  onPointerLeave,
  ...buttonProps
}: LiquidCreateButtonProps) {
  const [hovered, setHovered] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const displacementScale = useAnimatedDisplacement(hovered ? 42 : 28, reducedMotion);
  const unavailable = disabled || loading;

  const handleMouseEnter = (event: MouseEvent<HTMLButtonElement>) => {
    if (!unavailable) setHovered(true);
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLButtonElement>) => {
    setHovered(false);
    onMouseLeave?.(event);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!unavailable && !hovered) setHovered(true);
    onPointerMove?.(event);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLButtonElement>) => {
    setHovered(false);
    onPointerLeave?.(event);
  };

  return (
    <button
      {...buttonProps}
      type={type}
      disabled={unavailable}
      aria-busy={loading || undefined}
      data-hovered={hovered || undefined}
      data-displacement-scale={displacementScale.toFixed(1)}
      className={`liquid-create-button liquid-create-button--${variant} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <span className="liquid-create-button__edge-glow" aria-hidden="true" />
      <span className="liquid-create-button__lens" aria-hidden="true">
        <LiquidGlass
          overLight
          mode="standard"
          displacementScale={displacementScale}
          blurAmount={0.075}
          saturation={112}
          aberrationIntensity={0.35}
          elasticity={reducedMotion ? 0 : 0.03}
          cornerRadius={15}
          padding="0"
          className="liquid-create-button__liquid"
        >
          <span />
        </LiquidGlass>
      </span>

      <span className="liquid-create-button__caustic" aria-hidden="true" />

      <span className="liquid-create-button__content">
        {loading && (
          <Loader2
            size={16}
            className="liquid-create-button__loader"
            aria-hidden="true"
          />
        )}
        {!loading && leadingIcon}
        <span aria-live="polite">{loading ? loadingLabel : label}</span>
        {!loading && showArrow && (
          <ArrowRight
            size={15}
            className="liquid-create-button__arrow"
            aria-hidden="true"
          />
        )}
      </span>
    </button>
  );
}
